import React, { useState, useEffect, useCallback, useRef } from 'react';
import T from 'i18n';
import { Button, Fab, Grid, Tooltip, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { ChatOutlined, History, Share, MoreHoriz } from '@mui/icons-material';
import SearchInput from './SearchInput';
import { NewSearchContainer } from './NewSearchContainer';
import SearchContainer from './SearchContainer';
import { useFetchMatches } from '../redux/fetchMatches';
import { useDispatch, useSelector } from 'react-redux';
import { useGenerateAnswers } from '../redux/generateAnswers';
import { selectAiToolsFilters, selectParams } from 'common/selectors';
import SearchHistory from './SearchHistory';
import { useFetchSearchHistoryItem } from '../redux/fetchSearchHistoryItem';
import { useFetchFileSearchHistory } from '../redux/fetchFileSearchHistory';
import { useUpdateFileSearchHistoryItem } from '../redux/updateFileSearchHistoryItem';
import { Spinner } from 'features/common';
import history from 'common/history';
import SearchInfo from './SearchInfo';
import { setAiToolsFilters } from '../redux/setAiToolsFilters';
import { getSelectedLocation } from './SearchInputLocations';
import { useSetAISearchState } from '../redux/setAISearchState';
import useAIHandler from './hooks/useAIHandler';

interface SearchCenterProps {
  fileSidebarAskAI: boolean;
  fileData: any;
  viewerControl: any;
  gridView?: boolean;
}

const SearchCenter: React.FC<SearchCenterProps> = ({
  fileSidebarAskAI,
  fileData,
  viewerControl,
  gridView,
}) => {
  const dispatch = useDispatch();
  const { clearMatches, fetchDocumentsPending, cancelFetchMatches } = useFetchMatches();
  const { clearAnswers, generateAnswersPending, cancelGenerateAnswers } = useGenerateAnswers();
  const { searchMatchesInput, searchAnsChat, clearAISearchState } = useSetAISearchState();
  const { file, aiSearchId } = useSelector(selectParams);
  const alreadySelectedFilters = useSelector(selectAiToolsFilters);
  const { fetchSearchHistoryItemPending, fetchSearchHistoryItem, cancelFetchSearchHistoryItem } =
    useFetchSearchHistoryItem();
  const {
    fileSearchHistory,
    fetchFileSearchHistoryPending,
    fetchFileSearchHistory,
    cancelFetchFileSearchHistory,
  } = useFetchFileSearchHistory();
  const { updateFileSearchHistoryItem, updateFileSearchHistoryItemPending } =
    useUpdateFileSearchHistoryItem();
  const [loading, setLoading] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [continueChatInput, setContinueChatInput] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const isMenuOpen = Boolean(menuAnchorEl);
  const latestFileSearchHistory =
    fileSidebarAskAI && fileSearchHistory ? fileSearchHistory[0] : null;

  // Determine if we're on a transcript page
  const isTranscriptPage = history.location.pathname.includes('-transcript');
  const open = Boolean(anchorEl);
  const {
    selectedMatches,
    setSelectedMatches,
    filters,
    setFilters,
    processTopic,
    isNewSearch,
    setIsNewSearch,
    processQuestions,
    showSearchInp,
    askAIHandler,
    matches,
    saveSearchInputs,
    handleSearchHistoryResponse,
    updateScrollbar,
    isAnswersMode,
    perfectScrollbarRef,
    showSuggestedQuestions,
    showNoMatchesMessage,
    storedOutputType,
  } = useAIHandler();

  // Track which aiSearchId we've already processed to prevent infinite loops
  const processedSearchIdRef = useRef<string | null>(null);

  // Track previous URL to detect navigation from specific search to base ai-tools
  const previousUrlRef = useRef<string | null>(null);
  const currentPathname = history.location.pathname;

  // Determine if archive menu should be hidden based on API call states
  const isArchiveDisabled =
    updateFileSearchHistoryItemPending ||
    fetchFileSearchHistoryPending ||
    fetchSearchHistoryItemPending ||
    fetchDocumentsPending ||
    isArchiving ||
    !searchAnsChat?.length ||
    generateAnswersPending;

  useEffect(() => {
    return () => {
      // Cancel any ongoing search history requests when component unmounts
      try {
        cancelFetchFileSearchHistory();
      } catch (error) {
        // Silently ignore any cancellation errors
        console.log('File search history cancellation completed');
      }

      try {
        cancelFetchSearchHistoryItem();
      } catch (error) {
        // Silently ignore any cancellation errors
        console.log('Search history item cancellation completed');
      }

      try {
        cancelGenerateAnswers();
      } catch (error) {
        // Silently ignore any cancellation errors
        console.log('Generate answers cancellation completed');
      }

      try {
        cancelFetchMatches();
      } catch (error) {
        // Silently ignore any cancellation errors
        console.log('Fetch matches cancellation completed');
      }
    };
  }, [
    cancelFetchFileSearchHistory,
    cancelFetchSearchHistoryItem,
    cancelGenerateAnswers,
    cancelFetchMatches,
  ]);

  // Monitor URL changes to detect navigation from specific search to base ai-tools
  useEffect(() => {
    const previousUrl = previousUrlRef.current;
    const currentUrl = currentPathname;

    // Check if we're navigating from a specific search (/ai-tools/searchId) to base ai-tools (/ai-tools)
    if (
      previousUrl &&
      previousUrl.includes('/ai-tools/') && // Previous URL had a search ID
      currentUrl.endsWith('/ai-tools') && // Current URL is base ai-tools
      !currentUrl.includes('/ai-tools/') // Current URL doesn't have a search ID
    ) {
      // Reset all component state to simulate a fresh mount
      setIsNewSearch(true);
      processedSearchIdRef.current = null;
      clearMatches();
      clearAnswers();
      clearAISearchState();
      setSelectedMatches([]);
      setFilters({
        searchHistoryId: '',
        phrase: '',
        location: [],
        files: [],
        question: '',
        triggerSearch: false,
      });
    }

    // Update previous URL reference
    previousUrlRef.current = currentUrl;
  }, [
    currentPathname,
    clearMatches,
    clearAnswers,
    clearAISearchState,
    setSelectedMatches,
    setFilters,
    setIsNewSearch,
  ]);

  useEffect(() => {
    if (fileSidebarAskAI && latestFileSearchHistory?.id && !latestFileSearchHistory?.archive) {
      fetchSearchHistoryItem({
        searchHistoryItmId: latestFileSearchHistory.id,
        omitFiles: true,
      }).then((res: any) => {
        if (!res?.cancelled) {
          handleSearchHistoryResponse(res, latestFileSearchHistory?.id, fileSidebarAskAI);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSearchHistoryItem, latestFileSearchHistory?.id]);

  // Fetch search history for the specific file
  useEffect(() => {
    if (fileData?.id && fileSidebarAskAI) {
      fetchFileSearchHistory(fileData.id);
    }
  }, [fileData?.id, fileSidebarAskAI, fetchFileSearchHistory]);

  const handleSearchItemClick = useCallback(
    (searchHistoryItmId: string) => {
      setLoading(true);
      handleSearchHistoryClose();
      clearAISearchState();
      fetchSearchHistoryItem({
        searchHistoryItmId,
        omitFiles: true,
      })
        .then((res: any) => {
          handleSearchHistoryResponse(res, searchHistoryItmId);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchSearchHistoryItem, handleSearchHistoryResponse, clearAISearchState],
  );

  const handleSearch = (input: string, filters: any, outputType: string = 'list') => {
    saveSearchInputs(input);
    updateScrollbar();
    if (fileSidebarAskAI) {
      setContinueChatInput(true);
    }
    const containsMatches = searchMatchesInput?.find((itm: any) => itm.matches);
    const containsQues = searchAnsChat.length > 0;
    const inputVal = input;
    const totalMatches = containsMatches?.matches?.length || 0;
    if (!isNewSearch && (totalMatches > 0 || containsQues)) {
      if (fileSidebarAskAI) {
        processQuestions(null, inputVal, fileSidebarAskAI, false, outputType);
      } else {
        processQuestions(null, inputVal, undefined, undefined, outputType);
      }
    } else {
      processTopic(true, fileSidebarAskAI, inputVal, '', fileData, filters, outputType);
    }
  };

  const handleSearchHistoryClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSearchHistoryClose = () => {
    setAnchorEl(null);
  };

  const handleNewSearchClicked = useCallback(() => {
    setIsNewSearch(true);
    processedSearchIdRef.current = null; // Reset processed search ID when starting new search
    history.push(history.location.pathname.replace(/\/ai-tools.*/, ``) + `/ai-tools`);
    const topicLocation = searchMatchesInput?.location || [];
    clearMatches();
    clearAnswers();
    setFilters({
      searchHistoryId: '',
      phrase: '',
      location: topicLocation,
      files: [],
      question: '',
      triggerSearch: false,
    });
    clearAISearchState();
    setSelectedMatches([]);
    const searchInp = document.getElementById('ai-search-input');
    if (searchInp) {
      searchInp.click();
    }
  }, [
    clearMatches,
    clearAnswers,
    setFilters,
    clearAISearchState,
    setSelectedMatches,
    searchMatchesInput,
  ]);

  const copySearchUrl = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setShowShareTooltip(true);
    });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleArchiveClick = () => {
    // Set local archiving state immediately for fast spinner response
    setIsArchiving(true);

    // Helper function to get the latest search history item
    const getLatestSearchHistoryItem = () => {
      if (fileSearchHistory?.length > 0) {
        return fileSearchHistory[0];
      }
      if (searchMatchesInput?.length > 0) {
        return searchMatchesInput[0];
      }
      return null;
    };

    // Helper function to determine if item is from file search history
    const isFromFileSearchHistory = (item: any) => {
      return fileSearchHistory?.length > 0 && item === fileSearchHistory[0];
    };

    // Helper function to build search history item for update
    const buildSearchHistoryItem = (item: any, isFromFileHistory: boolean) => {
      return {
        id: isFromFileHistory ? item.id : item.searchHistoryId,
        archive: true,
        name: isFromFileHistory ? item.name : `${fileData.id} : ${item.phrase}`,
      };
    };

    const latestItem = getLatestSearchHistoryItem();
    if (latestItem) {
      const isFromFileHistory = isFromFileSearchHistory(latestItem);
      const searchHistoryItem = buildSearchHistoryItem(latestItem, isFromFileHistory);

      updateFileSearchHistoryItem({ searchHistoryItem })
        .then(() => {
          // after successful archive, refetch the new search history from the backend
          fetchFileSearchHistory(fileData.id);
          // Clear AI search state after successful archive
          clearAISearchState();
          // fileSearchHistory state will be automatically updated by the reducer
        })
        .catch((error: any) => {
          console.error('Failed to archive search history item:', error);
        })
        .finally(() => {
          // Reset local archiving state when operation completes
          setIsArchiving(false);
        });
    } else {
      // Reset archiving state if no item to archive
      setIsArchiving(false);
    }

    handleMenuClose();
  };

  //TO-DO: Refactor all useEffect

  useEffect(() => {
    if (
      aiSearchId &&
      !loading &&
      searchMatchesInput?.length === 0 &&
      searchAnsChat?.length === 0 &&
      processedSearchIdRef.current !== aiSearchId // Only process if we haven't processed this ID yet
    ) {
      processedSearchIdRef.current = aiSearchId; // Mark this ID as processed
      handleSearchItemClick(aiSearchId);
    }
  }, [aiSearchId, handleSearchItemClick, loading, searchAnsChat, searchMatchesInput]);

  useEffect(() => {
    if (showShareTooltip) {
      setTimeout(() => {
        setShowShareTooltip(false);
      }, 1500);
    }
    return () => {
      clearAISearchState();
      processedSearchIdRef.current = null; // Reset when clearing AI search state
    };
  }, [showShareTooltip, clearAISearchState]);

  useEffect(() => {
    if (
      (showSuggestedQuestions || fileSidebarAskAI) &&
      !isNewSearch &&
      filters.triggerSearch &&
      searchMatchesInput.find((itm: any) => itm.matches)
    ) {
      const suggestedQuestion =
        'Can you suggest 5 questions based on these extracts. List the questions without numbering them.';
      setFilters({ ...filters, phrase: '', triggerSearch: false });
      processQuestions(
        filters.phrase === suggestedQuestion,
        '',
        fileSidebarAskAI,
        true,
        storedOutputType,
      );
    }
  }, [
    isNewSearch,
    filters,
    processQuestions,
    showSuggestedQuestions,
    searchMatchesInput,
    searchAnsChat,
    setFilters,
    fileSidebarAskAI,
    storedOutputType,
  ]);

  useEffect(() => {
    if (alreadySelectedFilters && alreadySelectedFilters.folder) {
      setFilters({
        ...filters,
        location: [...getSelectedLocation(alreadySelectedFilters.folder)],
      });
      dispatch(setAiToolsFilters({}));
    } else if (alreadySelectedFilters && alreadySelectedFilters.files) {
      setFilters({
        ...filters,
        files: [...alreadySelectedFilters.files],
      });
      dispatch(setAiToolsFilters({}));
    }
  }, [alreadySelectedFilters, filters, dispatch, setFilters]);
  if (
    fetchSearchHistoryItemPending ||
    updateFileSearchHistoryItemPending ||
    fetchFileSearchHistoryPending ||
    isArchiving ||
    (searchAnsChat.length === 2 && searchAnsChat[1]?.firstQuery)
  ) {
    return <Spinner />;
  }
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '0.6rem',
          padding: '0 2rem 0.5rem 0',
        }}
      >
        {!isArchiveDisabled && fileSidebarAskAI && (
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              padding: '2px',
              minWidth: 'auto',
              backgroundColor: '#F7F7F8',
              '&:hover': {
                backgroundColor: '#E5E5E5',
              },
            }}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        )}
      </div>
      <Grid
        container
        width="100%"
        sx={{
          height: fileSidebarAskAI ? 'auto' : '100%',
          maxHeight: 'calc(100vh - 10rem)',
          paddingLeft: fileSidebarAskAI ? '0' : '1rem',
        }}
      >
        {showNoMatchesMessage && (
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              color: '#777',
              textAlign: 'left',
              width: '100%',
              padding: '2rem 1rem 2rem 0',
            }}
          >
            {T.translate('feedback.fetchMatchesNoResults')}
          </Typography>
        )}
        {!fileSidebarAskAI && (
          <Grid
            item
            xs={12}
            sx={{ height: '2.5rem' }}
            display={'flex'}
            justifyContent={'space-between'}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<History fontSize="small" />}
              onClick={handleSearchHistoryClick}
            >
              {T.translate('case.searchHistory')}
            </Button>
            <div>
              {!isNewSearch && (
                <>
                  <Tooltip
                    placement="bottom"
                    onClose={() => setShowShareTooltip(false)}
                    open={showShareTooltip}
                    onOpen={() => setTimeout(() => setShowShareTooltip(false), 1000)}
                    disableHoverListener
                    title={T.translate('generic.copytoClipboard')}
                    arrow
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: '0.5rem', height: '2.5rem' }}
                      startIcon={<Share fontSize="small" />}
                      onClick={copySearchUrl}
                    >
                      {T.translate('case.shareSearchUrl')}
                    </Button>
                  </Tooltip>
                  <Button
                    id="new-search-button"
                    variant="outlined"
                    size="small"
                    sx={{ height: '2.5rem' }}
                    startIcon={<ChatOutlined fontSize="small" />}
                    onClick={handleNewSearchClicked}
                  >
                    {T.translate('case.newSearch')}
                  </Button>
                </>
              )}
            </div>
          </Grid>
        )}

        {!file && <Grid item xs={0} sm={1} md={2} lg={2} />}
        <Grid
          item
          xs={12}
          sm={file ? 12 : 10}
          md={file ? 12 : isNewSearch && !fileSidebarAskAI ? 9 : 8}
          lg={file ? 12 : isNewSearch && !fileSidebarAskAI ? 9 : 8}
          height={`calc(100% - ${isNewSearch ? '3rem' : '14rem'})`}
        >
          {loading ? (
            <Spinner />
          ) : isNewSearch || (fileSidebarAskAI && !searchAnsChat?.length) ? (
            <>
              <NewSearchContainer
                filters={filters}
                handleSearch={handleSearch}
                askAIHandler={askAIHandler}
                fileSidebarAskAI={fileSidebarAskAI}
                file={file}
              />
            </>
          ) : (
            <SearchContainer
              selectedMatches={selectedMatches}
              scrollBarRef={perfectScrollbarRef}
              searchContainerId={filters.searchHistoryId}
              startNewSearch={handleNewSearchClicked}
              processQuestions={processQuestions}
              askAIHandler={askAIHandler}
              fileSidebarAskAI={fileSidebarAskAI}
              viewerControl={viewerControl}
              gridView={gridView}
              isTranscriptPage={isTranscriptPage}
            />
          )}
        </Grid>
        {!file && <Grid item xs={0} sm={1} md={2} lg={2} />}
        {(continueChatInput ||
          (!isNewSearch &&
            !fetchDocumentsPending &&
            searchMatchesInput &&
            (searchMatchesInput.find((itm: any) => itm.matches?.length > 0) ||
              searchMatchesInput.find((itm: any) => itm.documents?.length > 0) ||
              showSearchInp))) &&
          !(fileSidebarAskAI && searchAnsChat?.length === 0) && (
            <>
              {!file && <Grid item xs={0} sm={1} md={2} lg={2} />}
              <Grid
                item
                xs={12}
                sm={file ? 12 : 10}
                md={file ? 12 : isNewSearch && !fileSidebarAskAI ? 9 : 8}
                lg={file ? 12 : isNewSearch && !fileSidebarAskAI ? 9 : 8}
                sx={{
                  ...(fileSidebarAskAI
                    ? {
                        position: 'absolute',
                        bottom: gridView ? '1rem' : '2rem',
                        left: 0,
                        right: 0,
                        padding: '0 1rem 0 0',
                        zIndex: 1001,
                      }
                    : {}),
                }}
              >
                <SearchInput
                  handleSearch={handleSearch}
                  matches={matches}
                  isNewSearch={false}
                  containsMatches={!!searchMatchesInput.find((itm: any) => itm.matches)}
                  isAnswersMode={isAnswersMode}
                  fileSidebarAskAI={fileSidebarAskAI}
                  gridView={gridView}
                  file={file}
                />
              </Grid>
              {!file && !fileSidebarAskAI && isNewSearch && (
                <Grid
                  item
                  xs={0}
                  sm={1}
                  md={isNewSearch && !fileSidebarAskAI ? 1.5 : 2}
                  lg={isNewSearch && !fileSidebarAskAI ? 1.5 : 2}
                  sx={{ textAlign: 'end', alignContent: 'end' }}
                >
                  <Fab size="small" sx={{ backgroundColor: 'whitesmoke', mb: '1rem' }}>
                    <SearchInfo isNewSearch={false} />
                  </Fab>
                </Grid>
              )}
            </>
          )}
        {open && anchorEl && !fileSidebarAskAI && (
          <SearchHistory
            open={open}
            anchorEl={anchorEl}
            handleSearchHistoryClose={handleSearchHistoryClose}
            handleSearchItemClick={handleSearchItemClick}
          />
        )}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleArchiveClick}>{T.translate('generic.archive')}</MenuItem>
        </Menu>
      </Grid>
    </>
  );
};

export default SearchCenter;
